import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { CheckCircle2, ChevronLeft, ChevronRight, Clock, OctagonAlert, TriangleAlert } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AppLink } from "@/components/AppLink";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useStore } from "@/hooks/useStore";
import { alarm as alarmApi } from "@/endpoints/dryp/alarm";
import { cn } from "@/lib/utils";
import { DELIVERY_MODE_LABELS, COOLDOWN_OPTIONS, formatAlarmCondition } from "../types";
import type { Alarm } from "../types";

interface AlarmDetailProps {
	alarm: Alarm;
}

const PAGE_SIZE = 5;

// Force UTC interpretation: if the ISO string has no timezone marker (Z or ±hh:mm),
// the runtime would parse it as local time. The backend timestamps are UTC, so we
// append `Z` when missing — `formatDistanceToNowStrict` then computes the correct
// distance against the local clock.
const parseUtc = (iso: string): Date => {
	const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
	return new Date(hasTimezone ? iso : `${iso}Z`);
};
const timeAgo = (iso: string): string => formatDistanceToNowStrict(parseUtc(iso), { addSuffix: true });

const formatMeasurement = (value: number): string => value.toFixed(2);

const SEVERITY_ROW: Record<Dryp.AlarmSeverity, { container: string; bar: string; iconWrap: string; value: string }> = {
	critical: {
		container:
			"border-red-200 bg-gradient-to-r from-red-50 to-transparent hover:border-red-300 dark:border-red-900/40 dark:from-red-950/30",
		bar: "bg-red-500",
		iconWrap: "bg-red-100 text-red-600 dark:bg-red-950/50 dark:text-red-400",
		value: "text-red-700 dark:text-red-400",
	},
	warning: {
		container:
			"border-amber-200 bg-gradient-to-r from-amber-50 to-transparent hover:border-amber-300 dark:border-amber-900/40 dark:from-amber-950/30",
		bar: "bg-amber-500",
		iconWrap: "bg-amber-100 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400",
		value: "text-amber-700 dark:text-amber-400",
	},
	ok: {
		container:
			"border-emerald-200 bg-gradient-to-r from-emerald-50/60 to-transparent hover:border-emerald-300 dark:border-emerald-900/40 dark:from-emerald-950/20",
		bar: "bg-emerald-500/70",
		iconWrap: "bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400",
		value: "",
	},
};

const severityIcon = (severity: Dryp.AlarmSeverity) =>
	severity === "ok" ? CheckCircle2 : severity === "critical" ? OctagonAlert : TriangleAlert;

export const AlarmDetail = observer(({ alarm }: AlarmDetailProps) => {
	const statesApi = useAsyncStateStore(alarmApi.getAlarmStates);
	const {
		uiStore: { sheetStore },
	} = useStore();
	const [page, setPage] = useState(0);

	useEffect(() => {
		statesApi.fetch({ pageNumber: page, pageSize: PAGE_SIZE }, undefined, {
			alarmIds: [alarm.id],
		});
	}, [alarm.id, page]);

	const states = statesApi.data?.items ?? [];
	const totalItems = statesApi.data?.query?.pagination.totalItems ?? 0;
	const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
	const cooldownLabel =
		COOLDOWN_OPTIONS.find(option => option.value === alarm.cooldownMinutes)?.label ?? `${alarm.cooldownMinutes}min`;
	// Skeleton on first load only — for subsequent page changes keep stale rows visible and dim them.
	const showSkeleton = statesApi.loading && !statesApi.loaded;

	return (
		<div className="flex flex-col flex-1 overflow-y-auto p-6">
			<SheetHeader>
				<SheetTitle>{alarm.name}</SheetTitle>
			</SheetHeader>

			<div className="grid grid-cols-4 gap-3 py-4 border-b">
				<div className="text-center">
					<p className="text-2xl font-semibold">{alarm.counts.structures}</p>
					<p className="text-xs text-muted-foreground">Monitored</p>
				</div>
				<div className="text-center">
					<p className="text-2xl font-semibold text-amber-500">{alarm.counts.warning ?? 0}</p>
					<p className="text-xs text-muted-foreground">Warning</p>
				</div>
				<div className="text-center">
					<p className="text-2xl font-semibold text-red-500">{alarm.counts.critical ?? 0}</p>
					<p className="text-xs text-muted-foreground">Critical</p>
				</div>
				<div className="text-center">
					<p className="text-2xl font-semibold">{alarm.counts.recipients}</p>
					<p className="text-xs text-muted-foreground">Recipients</p>
				</div>
			</div>

			<div className="py-4 border-b space-y-2">
				<h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Configuration</h4>
				<dl className="divide-y divide-border/60">
					<div className="flex items-start justify-between gap-4 py-2">
						<dt className="shrink-0 text-xs text-muted-foreground">Condition</dt>
						<dd className="text-right text-xs font-medium">{formatAlarmCondition(alarm)}</dd>
					</div>
					<div className="flex items-start justify-between gap-4 py-2">
						<dt className="shrink-0 text-xs text-muted-foreground">Notification</dt>
						<dd className="text-right text-xs font-medium">
							{alarm.warningThreshold != null && alarm.warningDeliveryMode
								? `${DELIVERY_MODE_LABELS[alarm.criticalDeliveryMode]} (critical) · ${DELIVERY_MODE_LABELS[alarm.warningDeliveryMode]} (warning)`
								: DELIVERY_MODE_LABELS[alarm.criticalDeliveryMode]}
						</dd>
					</div>
					<div className="flex items-start justify-between gap-4 py-2">
						<dt className="shrink-0 text-xs text-muted-foreground">Cooldown</dt>
						<dd className="text-right text-xs font-medium">{cooldownLabel}</dd>
					</div>
				</dl>
			</div>

			<div className="flex-1 py-4">
				<div className="flex items-center justify-between mb-3">
					<h4 className="text-sm font-medium">Structures in alarm</h4>
					{totalItems > 0 && <span className="text-xs text-muted-foreground">{totalItems} total</span>}
				</div>

				{showSkeleton ? (
					<div className="space-y-2">
						<Skeleton className="h-20 w-full" />
						<Skeleton className="h-20 w-full" />
						<Skeleton className="h-20 w-full" />
					</div>
				) : states.length === 0 ? (
					<div className="rounded-md border border-dashed p-8 text-center space-y-1">
						<p className="text-sm font-medium text-muted-foreground">No data to evaluate yet</p>
						<p className="text-xs text-muted-foreground">
							No measurements have arrived yet for this alarm&apos;s structures to be checked against the
							condition.
						</p>
					</div>
				) : (
					<div className={cn("space-y-2 transition-opacity", statesApi.loading && "opacity-50")}>
						{states.map(state => {
							const style = SEVERITY_ROW[state.severity];
							const Icon = severityIcon(state.severity);
							const isBad = state.severity !== "ok";
							return (
								<AppLink
									key={`${state.alarm.id}-${state.structure.id}`}
									to="Dashboard.Structures.Structure"
									params={{ id: state.structure.id }}
									onClick={() => sheetStore.close()}
									className={cn(
										"group relative flex items-start gap-3 overflow-hidden rounded-lg border p-3 pl-4 transition-all hover:shadow-sm",
										style.container,
									)}
								>
									<span
										aria-hidden="true"
										className={cn("absolute inset-y-0 left-0 w-1", style.bar)}
									/>
									<div
										className={cn(
											"flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
											style.iconWrap,
										)}
									>
										<Icon className="h-4 w-4" />
									</div>
									<div className="flex-1 min-w-0">
										<p className="text-sm font-semibold truncate group-hover:text-primary">
											{state.structure.name}
										</p>
										{isBad && state.triggerValue != null && (
											<p className="mt-1 text-xs text-muted-foreground">
												Triggered value:{" "}
												<span className={cn("text-sm font-semibold", style.value)}>
													{formatMeasurement(state.triggerValue)}{" "}
													{state.parameter.unit.symbol}
												</span>
											</p>
										)}
										<div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground">
											{isBad && state.triggeredAt && (
												<span className="inline-flex items-center gap-1">
													<Clock className="h-3 w-3" /> Triggered {timeAgo(state.triggeredAt)}
												</span>
											)}
											<span>Checked {timeAgo(state.lastEvaluatedAt)}</span>
										</div>
									</div>
									<ChevronRight className="h-4 w-4 self-center shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
								</AppLink>
							);
						})}
					</div>
				)}

				{totalPages > 1 && (
					<div className="mt-4 flex items-center justify-between">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setPage(prev => Math.max(0, prev - 1))}
							disabled={page === 0 || statesApi.loading}
						>
							<ChevronLeft className="h-4 w-4 mr-1" />
							Previous
						</Button>
						<span className="text-xs text-muted-foreground">
							Page {page + 1} of {totalPages}
						</span>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setPage(prev => Math.min(totalPages - 1, prev + 1))}
							disabled={page >= totalPages - 1 || statesApi.loading}
						>
							Next
							<ChevronRight className="h-4 w-4 ml-1" />
						</Button>
					</div>
				)}
			</div>
		</div>
	);
});
